import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Schedule,
  LocationOn,
  Groups,
  Visibility,
  Home,
  Lock,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';

interface ShuttleSchedule {
  id: string;
  departure_date: string;
  departure_time: string;
  pickup_location: string;
  destination: string;
  available_seats: number;
  status: string;
  shuttle: {
    name: string;
    capacity: number;
  };
  bookings_count: number;
  total_seats_booked: number;
}

interface Club {
  id: string;
  club_name: string;
}

const DriverLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n, ready } = useTranslation();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [selectedClubId, setSelectedClubId] = useState<string>('');
  const [schedules, setSchedules] = useState<ShuttleSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
  const preselectedClub = searchParams.get('club');
  const dateLocale = i18n.language === 'it' ? it : enUS;


  const fetchClubs = useCallback(async () => {
    if (!preselectedClub) return; // Safety check, shouldn't happen due to useEffect condition
    
    try {
      const response = await fetch(`${apiUrl}/public/clubs`);
      if (!response.ok) throw new Error('Failed to fetch clubs');
      const data = await response.json();
      
      // Only work with the preselected club
      const club = data.find((c: Club) => c.id === preselectedClub);
      if (club) {
        setClubs([club]); // Only show the preselected club
        setSelectedClubId(preselectedClub);
      } else {
        // Club not found
        setError(t('driver.invalidClubLink'));
      }
    } catch (error: any) {
      console.error('Failed to fetch clubs:', error);
      const errorMessage = error?.message || t('shuttle.errors.fetchClubs');
      setError(typeof errorMessage === 'string' ? errorMessage : t('shuttle.errors.fetchClubs'));
    }
  }, [preselectedClub, t, apiUrl]);

  const fetchSchedules = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all upcoming schedules (no date filter)
      const response = await fetch(
        `${apiUrl}/public/clubs/${selectedClubId}/schedules/upcoming`
      );
      
      if (!response.ok) throw new Error('Failed to fetch schedules');
      
      const data = await response.json();
      // Sort by date and time (most recent first)
      const sortedData = data.sort((a: ShuttleSchedule, b: ShuttleSchedule) => {
        const dateA = new Date(a.departure_date + 'T' + a.departure_time);
        const dateB = new Date(b.departure_date + 'T' + b.departure_time);
        return dateA.getTime() - dateB.getTime();
      });
      setSchedules(sortedData);
    } catch (error: any) {
      console.error('Failed to fetch schedules:', error);
      const errorMessage = error?.message || t('shuttle.errors.fetchSchedules');
      setError(typeof errorMessage === 'string' ? errorMessage : t('shuttle.errors.fetchSchedules'));
    } finally {
      setLoading(false);
    }
  }, [selectedClubId, t, apiUrl]);

  useEffect(() => {
    // Only fetch clubs if a club ID is provided in the URL
    if (preselectedClub) {
      fetchClubs();
    }
  }, [preselectedClub, fetchClubs]);

  useEffect(() => {
    if (selectedClubId) {
      // Check if we have stored authentication for this club
      const storedAuth = sessionStorage.getItem(`driver_auth_${selectedClubId}`);
      if (storedAuth) {
        setIsAuthenticated(true);
      } else {
        // Show PIN dialog
        setPinDialogOpen(true);
      }
    }
  }, [selectedClubId]);

  useEffect(() => {
    if (selectedClubId && isAuthenticated) {
      fetchSchedules();
    }
  }, [selectedClubId, isAuthenticated, fetchSchedules]);

  const handlePinSubmit = async () => {
    try {
      setPinError('');
      const response = await fetch(`${apiUrl}/public/driver/verify-pin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          club_id: selectedClubId,
          pin: pin,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        // Handle different error response formats
        let errorMessage = t('driver.invalidPin');
        
        if (typeof error === 'string') {
          errorMessage = error;
        } else if (Array.isArray(error)) {
          // Handle Pydantic validation errors
          errorMessage = error[0]?.msg || error[0]?.message || t('driver.invalidPin');
        } else if (error.detail) {
          errorMessage = error.detail;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setPinError(errorMessage);
        return;
      }

      // Store authentication in session
      sessionStorage.setItem(`driver_auth_${selectedClubId}`, 'true');
      setIsAuthenticated(true);
      setPinDialogOpen(false);
      setPin('');
    } catch (error) {
      console.error('Error verifying PIN:', error);
      setPinError(t('driver.pinVerificationFailed'));
    }
  };


  const handleScheduleClick = (scheduleId: string) => {
    navigate(`/shuttle/${scheduleId}/driver`);
  };

  const getOccupancyColor = (schedule: ShuttleSchedule) => {
    const occupancy = (schedule.total_seats_booked / schedule.shuttle.capacity) * 100;
    if (occupancy >= 80) return 'error';
    if (occupancy >= 50) return 'warning';
    return 'success';
  };

  // Wait for translations to be ready
  if (!ready) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Check if club parameter is missing
  if (!preselectedClub) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Lock sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom color="error">
            {t('driver.accessDenied')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('driver.noDirectAccess')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('driver.contactAdmin')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            sx={{ mt: 3 }}
            startIcon={<Home />}
          >
            {t('common.backToHome')}
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 2, mb: 2 }}>
        {/* Club info and navigation */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
            {/* Display club name as read-only info */}
            {clubs.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('shuttle.selectedClub')}
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {clubs[0].club_name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/app/shuttle-booking')}
                  >
                    {t('shuttle.bookingPortal')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/')}
                    startIcon={<Home />}
                  >
                    {t('common.home')}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Schedules List */}
        {!loading && schedules.length === 0 && selectedClubId && (
          <Alert severity="info">
            {t('shuttle.noSchedules')}
          </Alert>
        )}

        {!loading && schedules.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">
              {t('driver.landing.upcomingShuttles') || "Upcoming Shuttles"}
            </Typography>
            
            {schedules.map((schedule) => {
              const departureDate = new Date(schedule.departure_date);
              return (
              <Card key={schedule.id}>
                <CardActionArea onClick={() => handleScheduleClick(schedule.id)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" component="div">
                        {schedule.shuttle.name}
                      </Typography>
                      <Chip
                        label={`${schedule.total_seats_booked}/${schedule.shuttle.capacity}`}
                        color={getOccupancyColor(schedule)}
                        size="small"
                      />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Schedule sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="h5" color="primary">
                          {schedule.departure_time}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {format(departureDate, 'EEEE, d MMMM yyyy', { locale: dateLocale })}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationOn sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {schedule.pickup_location} → {schedule.destination}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Groups sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {schedule.bookings_count} {t('driver.landing.bookings')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Visibility sx={{ mr: 0.5, fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
                          {t('driver.landing.viewDetails')}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            )})}
          </Box>
        )}

        {/* PIN Authentication Dialog */}
        <Dialog 
        open={pinDialogOpen} 
        onClose={() => {}} 
        maxWidth="xs" 
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Lock color="primary" />
          {t('driver.pinRequired')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3 }}>
            {t('driver.enterPinToContinue')}
          </Typography>
          <TextField
            fullWidth
            label={t('driver.pin')}
            value={pin}
            onChange={(e) => {
              setPin(e.target.value);
              setPinError('');
            }}
            type="password"
            inputProps={{ maxLength: 4, pattern: '[0-9]*' }}
            error={!!pinError}
            helperText={pinError}
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter' && pin.length === 4) {
                handlePinSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handlePinSubmit}
            variant="contained"
            disabled={pin.length !== 4}
          >
            {t('driver.verify')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DriverLandingPage;